import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { Button } from '@components/Button';
import { Loader } from '@components/Loader';
import { CloseIcon } from '@icons/CloseIcon';
import { SegmentCreator } from '@insights_module/components/SegmentCreator';
import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { useSegmentCreatorContext } from '@insights_module/context/SegmentCreatorContext';
import { useIntegrationTags } from '@insights_module/hooks/useIntegrationTags';
import { useNotification } from '@insights_module/hooks/useNotification';
import { AQLQuery } from '@insights_module/adc_ql/types';
import { IntegrationType } from '@insights_module/types/Integrations';
import { createTagVersion } from '@insights_module/services/megaphone';
import { createQueryDataFromQuery } from '@insights_module/adc_ql/utils';
import {
  INTEGRATION_QUERY_KEYS,
  INTEGRATION_TYPE_MAPPER,
} from '@insights_module/constants/integrations';

const FormWrapper = tw.div`w-full max-w-[1090px] mx-auto bg-page-background rounded-lg text-text-and-icon border border-solid border-gray-1`;
const Title = tw.h2`font-medium text-sm`;
const Form = tw.form`w-full`;
const FormHeader = tw.div`w-full bg-neutral-background px-[24px] py-[8px] border-b border-solid border-gray-1 flex items-center justify-between rounded-t-lg`;
const FormBody = tw.div`w-full bg-page-background rounded-b-lg`;
const FormFooter = tw.div`w-full px-[24px] py-[8px] border-t border-solid border-gray-1 flex items-center justify-end mt-[8px] gap-[8px]`;
const InputsWrapper = tw.div`w-full px-[24px] py-[8px]`;
const InputLabel = tw.label`text-[12px] leading-[16px] text-text-and-icon-muted`;

const CloseButton = tw(
  Button.Primary
)`bg-transparent border border-solid border-gray-1 text-text-and-icon`;
const inputStyle = tw`block w-full text-[14px] text-text-and-icon bg-transparent border border-solid border-gray-3 rounded-md mb-2 p-[12px] leading-[150%]`;

type FormType = {
  display_name: string;
  description: string;
};

export const EditTagForm = ({ type }: { type: IntegrationType }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { query, setSegmentsData } = useSegmentCreatorContext();
  const { setIsEditTagModalOpen, tagToEdit, setTagToEdit, searchTerm } = useIntegrationsContext();
  const { getTagById } = useIntegrationTags(type, searchTerm);
  const { notify, NTFC_TYPE } = useNotification();
  const selectedTag = getTagById(tagToEdit);

  const { register, handleSubmit, setValue } = useForm<FormType>({});

  const handleClose = () => {
    setTagToEdit(null);
    setSegmentsData([]);
    setIsEditTagModalOpen(false);
  };

  const onSubmit = async (formData: FormType) => {
    if (!selectedTag?.id) return;

    setIsLoading(true);

    try {
      const data = await createTagVersion(selectedTag?.query.id, {
        ...formData,
        aql_query: query as AQLQuery,
      });

      queryClient.refetchQueries([
        INTEGRATION_QUERY_KEYS.INFINITE_TAGS,
        INTEGRATION_TYPE_MAPPER[type],
      ]);

      notify({ message: `Tag "${data.display_name}" updated.`, type: NTFC_TYPE.INFO });
      handleClose();
    } catch (error: unknown) {
      const err = error as { message?: string; status?: number };
      if (err.status === 409) {
        notify({ message: 'No changes detected.', type: NTFC_TYPE.DANGER });
      } else {
        notify({ message: err.message || 'Something went wrong.', type: NTFC_TYPE.DANGER });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTag) {
      setValue('display_name', selectedTag?.query.display_name);
      setValue('description', selectedTag.query.description);

      if (selectedTag.query.aql_query) {
        const query = createQueryDataFromQuery([selectedTag?.query.aql_query], null);
        setSegmentsData(query);
      }
    }
  }, [selectedTag]);

  if (type === 'megaphone') return null;

  return (
    <FormWrapper>
      <FormHeader>
        <Title>{t('insights.integrations.megaphone.edit_tag_modal.title')}</Title>
        <button onClick={handleClose}>
          <CloseIcon tw='fill-text-and-icon' />
        </button>
      </FormHeader>
      <FormBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputsWrapper>
            <InputLabel htmlFor='display_name'>
              {t('insights.integrations.megaphone.edit_tag_modal.title_input.label')}
            </InputLabel>
            <input {...register('display_name')} name='display_name' css={[inputStyle]} />
            <InputLabel htmlFor='description'>
              {t('insights.integrations.megaphone.edit_tag_modal.description_input.label')}
            </InputLabel>
            <textarea {...register('description')} name='description' css={[inputStyle]} />
            <SegmentCreator />
          </InputsWrapper>
          <FormFooter>
            <CloseButton type='button' onClick={handleClose}>
              {t('common.cancel')}
            </CloseButton>
            <Button.Primary type='submit' tw='min-w-[52px] flex items-center justify-center'>
              {isLoading ? (
                <Loader
                  theme='light'
                  size='sm'
                  tw='m-0 border-text-and-icon-invert! border-b-transparent!'
                />
              ) : (
                t('insights.integrations.megaphone.edit_tag_modal.cta')
              )}
            </Button.Primary>
          </FormFooter>
        </Form>
      </FormBody>
    </FormWrapper>
  );
};
